import React, { useEffect, useState } from 'react';
import { psApi, fmtMoney, fmtQty } from './psApi';

const LABEL = { added: 'Added', removed: 'Removed', qty: 'Quantity', rev: 'Revision', qty_rev: 'Qty + revision', same: 'Same' };
const side = (q, rev) => (q == null ? '' : `× ${fmtQty(q)}${rev ? ` · rev ${rev}` : ''}`);

/** The result of GET /compare: summary chips + one row per changed part (Baselines page and the branch compare). */
export function CompareResult({ cmp }) {
  const [showSame, setShowSame] = useState(false);
  const s = cmp.summary;
  const rows = cmp.rows.filter((r) => showSame || r.kind !== 'same');
  return (
    <>
      <div className="ps-sum">
        <span><b>{s.added}</b> added</span><span><b>{s.removed}</b> removed</span><span><b>{s.qty}</b> quantity changed</span>
        <span><b>{s.rev}</b> revision changed</span>
        <span>Cost <b>{fmtMoney(s.cost_from)}</b> → <b>{fmtMoney(s.cost_to)}</b> ({s.cost_to - s.cost_from >= 0 ? '+' : '−'}{fmtMoney(Math.abs(s.cost_to - s.cost_from))})</span>
        <button type="button" className="ps-btn ps-btn--link" onClick={() => setShowSame((v) => !v)}>{showSame ? 'Hide unchanged' : 'Show unchanged'}</button>
      </div>
      <div className="ps-tw">
        <div className="ps-thead ps-g-cmp"><span>Key</span><span>Name</span><span>Change</span><span>From</span><span>To</span></div>
        {rows.map((r) => (
          <div key={r.node} className="ps-trow ps-g-cmp">
            <span className="ps-ikey">{r.key}</span><span>{r.name}</span>
            <span className={`ps-chg ps-chg--${r.kind}`}>{LABEL[r.kind]}</span>
            <span>{side(r.from_qty, r.from_rev)}</span><span>{side(r.to_qty, r.to_rev)}</span>
          </div>
        ))}
      </div>
      {rows.length === 0 && <p className="ps-muted" style={{ marginTop: 10 }}>No differences.</p>}
    </>
  );
}

/**
 * The open branch compared with main or another open branch of the same product (2026-11-01).
 * From = the chosen one, To = this branch, so "Added" reads "added in this branch".
 */
export function BranchCompareModal({ root, branch, branches, initial, onClose, onAuth }) {
  const others = [{ id: 'current', label: 'Main' }]
    .concat(branches.filter((b) => b.status === 'open' && b.id !== branch.id).map((b) => ({ id: `br${b.id}`, label: `Branch: ${b.name}` })));
  const [with_, setWith] = useState(initial || 'current');
  const [cmp, setCmp] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let off = false;
    setCmp(null);
    setErr('');
    psApi.compare(root, with_, `br${branch.id}`).then((d) => { if (!off) setCmp(d); })
      .catch((e) => { if (!off && !onAuth(e)) setErr(e.message); });
    return () => { off = true; };
  }, [root, with_, branch.id, onAuth]);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  return (
    <div className="ps-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ps-modal ps-modal--wide" role="dialog" aria-modal="true" aria-label="Compare branch">
        <div className="ps-mhead"><h2>Compare {branch.name}</h2>
          <button type="button" className="ps-x" aria-label="Close" onClick={onClose}>✕</button></div>
        <div className="ps-mbody">
          <div className="ps-row">
            <span className="ps-lbl">With</span>
            {others.map((o) => (
              <button key={o.id} type="button" className={'ps-pill' + (o.id === with_ ? ' ps-pill--on' : '')}
                aria-pressed={o.id === with_} onClick={() => setWith(o.id)}>{o.label}</button>
            ))}
          </div>
          <p className="ps-muted ps-small" style={{ marginTop: 8 }}>
            From = {others.find((o) => o.id === with_)?.label || 'Main'} · To = this branch ({branch.name}).
          </p>
          {err && <div className="ps-error" role="alert">{err}</div>}
          {!cmp && !err && <p className="ps-muted">Comparing…</p>}
          {cmp && <CompareResult cmp={cmp} />}
        </div>
      </div>
    </div>
  );
}
